import {
  Box,
  CardContent,
  CardHeader,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  SearchBox,
  useModuleData,
  useModules,
} from '@schamburg-web/components';
import { ModuleConfig } from '@schamburg-web/interfaces';
import { useMap } from '@vis.gl/react-google-maps';
import React, { useCallback, useMemo } from 'react';

const SidebarDisplay: React.FC = () => {
  const { modules } = useModules();

  return (
    <Box
      className={'sidebar-map-control-container'}
      sx={{
        maxHeight: 'inherit',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
      }}
    >
      <Box
        className={'sidebar-type-container'}
        sx={{
          maxHeight: 'inherit',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          marginBottom: 2.5,
          borderRadius: '20px',
        }}
      >
        <Box
          sx={{
            paddingRight: 3,
            paddingLeft: 2,
            flexShrink: 0,
            marginBottom: 1,
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <SearchBox />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            paddingRight: 1,
          }}
        >
          {modules.map((module) => (
            <SidebarModuleDisplay key={module.name} module={module} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const SidebarModuleDisplay: React.FC<{ module: ModuleConfig }> = ({
  module,
}) => {
  // Use the isolated module data hook - this will only re-render when THIS module's data changes
  const { displayObjects, isEnabled } = useModuleData(module.name);
  const myCard = module.displayConfig?.cardDef || null;
  const map = useMap();
  const { toggleSidebar } = useModules();
  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up('md'));

  // Single shared click handler: always pans when coordinates exist;
  // on mobile also closes the sidebar.
  const handleCardClick = useCallback(
    (obj: any, e: React.MouseEvent) => {
      const target = e.target as HTMLElement;

      // On mobile, clicks inside the card header only expand/collapse — no pan or close.
      if (isMobile && target.closest('[data-card-header]')) return;

      // Button clicks always handle themselves (close button, etc.).
      if (target.closest('button')) return;

      const hasCoords = obj.iconPoint?.latitude && obj.iconPoint?.longitude;
      const shouldPan = isMobile || module.displayConfig?.panTo;

      if (hasCoords && shouldPan) {
        map?.panTo({
          lat: obj.iconPoint.latitude,
          lng: obj.iconPoint.longitude,
        });
        map?.setZoom(17);
      }
      if (isMobile) {
        toggleSidebar();
      }
    },
    [map, isMobile, toggleSidebar, module.displayConfig?.panTo]
  );

  const cards = useMemo(() => {
    if (!isEnabled || displayObjects.length === 0) return [];

    const sortedObjects = [...displayObjects];
    if (module.displayConfig?.sidebarSort) {
      sortedObjects.sort(module.displayConfig.sidebarSort);
    }

    // Filter out locationless objects before mapping
    const filteredObjects = sortedObjects.filter(
      (obj: any) => !obj.locationless
    );

    // Check if objects have impact levels
    const hasImpactLevels = filteredObjects.some(
      (obj: any) => obj.impact !== undefined
    );

    if (hasImpactLevels) {
      // Group by impact level
      const impactGroups = new Map<number, any[]>();

      filteredObjects.forEach((obj: any) => {
        const impact = obj.impact ?? 0;
        if (!impactGroups.has(impact)) {
          impactGroups.set(impact, []);
        }
        impactGroups.get(impact)?.push(obj);
      });

      // Sort impact levels descending (highest first)
      const sortedImpacts = Array.from(impactGroups.keys()).sort(
        (a, b) => b - a
      );

      // Map impact levels to descriptive labels
      const impactLabels: { [key: number]: string } = {
        0: 'No Impact',
        1: 'Low Impact',
        2: 'Medium Impact',
        3: 'High Impact',
        4: 'Closed',
      };

      return sortedImpacts.map((impact) => {
        const groupObjects = impactGroups.get(impact);
        if (!groupObjects) return null;
        return (
          <Paper
            key={`impact-${impact}`}
            sx={{ marginBottom: 2 }}
            className={'card-body'}
          >
            <CardHeader
              title={impactLabels[impact] || `Impact Level ${impact}`}
              sx={{
                paddingBottom: 1,
                paddingTop: 1,
                backgroundColor: 'action.hover',
                borderTopLeftRadius: 'inherit',
                borderTopRightRadius: 'inherit',
                '& .MuiCardHeader-title': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'text.primary',
                  letterSpacing: '0.5px',
                },
              }}
            />
            <CardContent sx={{ p: 0, '&:last-child': { pb: 1 } }}>
              {groupObjects.map((obj: any, index: number) => (
                <Box
                  key={obj.id}
                  onClick={(e) => handleCardClick(obj, e)}
                  sx={{
                    m: 0,
                    p: 0,
                    cursor: 'pointer',
                    borderColor: 'divider',
                    pb: index < groupObjects.length - 1 ? 1 : 0,
                    mb: index < groupObjects.length - 1 ? 1 : 0,
                  }}
                >
                  {myCard &&
                    React.createElement(myCard, {
                      icon: obj,
                      key: obj.id,
                      module: module,
                    })}
                </Box>
              ))}
            </CardContent>
          </Paper>
        );
      });
    }

    // No impact levels - render normally
    return filteredObjects.map((obj: any) => (
      <Box
        className={'card-container card-body'}
        key={obj.id}
        sx={{
          maxWidth: 500,
          width: '100%',
          cursor: 'pointer',
        }}
        onClick={(e) => handleCardClick(obj, e)}
      >
        {myCard &&
          React.createElement(myCard, {
            icon: obj,
            key: obj.id,
            module: module,
          })}
      </Box>
    ));
  }, [isEnabled, displayObjects, module, myCard, handleCardClick]);

  if (cards.length > 0) {
    return (
      <Box>
        <Box className={'sidebar-type-header'}>
          <Typography variant="h6">{module.moduleLabel}</Typography>
        </Box>
        {cards}
      </Box>
    );
  } else return null;
};

export default SidebarDisplay;
